import { useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import type { AppPagination } from "../types";

type TelegramAttachmentType = "none" | "image" | "voice" | "file";
type TelegramMessageStatus =
  | "pending"
  | "processing"
  | "sent"
  | "failed"
  | "stopped";

type BotNotificationLog = {
  id: number;
  userId: number;
  telegramChatId: string;
  text: string | null;
  attachmentType: TelegramAttachmentType;
  attachmentUrl: string | null;
  status: TelegramMessageStatus;
  telegramMessageId: number | null;
  sentAt: string | null;
  errorMessage: string | null;
  parseMode: string | null;
  disableWebPagePreview: boolean;
  broadcastJobId: number | null;
  createdAt: string;
  updatedAt: string;
};

const PAGE_SIZE = 20;

const fetchBotNotificationLogs = async (
  page: number,
  status: TelegramMessageStatus | "",
  userId: string,
  jobId: string,
): Promise<AppPagination<BotNotificationLog>> => {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
  });

  if (status) {
    query.set("status", status);
  }

  if (userId.trim()) {
    query.set("userId", userId.trim());
  }

  if (jobId.trim()) {
    query.set("jobId", jobId.trim());
  }

  const response = await axios.get<AppPagination<BotNotificationLog>>(
    `/admin/telegram-message-logs?${query.toString()}`,
  );

  return response.data;
};

const formatDate = (date?: string | null) => {
  if (!date) return "-";
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusColorMap: Record<
  TelegramMessageStatus,
  "default" | "warning" | "info" | "success" | "error"
> = {
  pending: "warning",
  processing: "info",
  sent: "success",
  failed: "error",
  stopped: "default",
};

export function BotNotificationsSection() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<TelegramMessageStatus | "">("");
  const [userId, setUserId] = useState("");
  const [jobId, setJobId] = useState("");

  const { data, isLoading, isError, error } = useQuery<
    AppPagination<BotNotificationLog>,
    Error
  >({
    queryKey: ["telegram-message-logs", page, status, userId, jobId],
    queryFn: () => fetchBotNotificationLogs(page, status, userId, jobId),
  });

  const items = data?.items ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: "white",
      }}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="h5" fontWeight="bold" color="text.secondary">
          Telegram Bot Notifications
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="bot-log-status">Status</InputLabel>
            <Select
              labelId="bot-log-status"
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as TelegramMessageStatus | "");
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="stopped">Stopped</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="User ID"
            placeholder="e.g. 120"
            value={userId}
            onChange={(event) => {
              setUserId(event.target.value.replace(/[^0-9]/g, ""));
              setPage(1);
            }}
          />

          <TextField
            size="small"
            label="Job ID"
            placeholder="e.g. 44"
            value={jobId}
            onChange={(event) => {
              setJobId(event.target.value.replace(/[^0-9]/g, ""));
              setPage(1);
            }}
          />
        </Stack>

        {isError ? (
          <Alert severity="error">
            {error.message || "Failed to load bot notification logs."}
          </Alert>
        ) : isLoading ? (
          <Stack spacing={1.5}>
            {[...Array(4)].map((_, idx) => (
              <Skeleton key={idx} variant="rounded" height={108} />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <Typography color="text.secondary">
            No bot notifications found.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {items.map((item) => (
              <Card key={item.id} variant="outlined">
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography fontWeight={700}>Log #{item.id}</Typography>
                    <Chip
                      size="small"
                      label={item.status.toUpperCase()}
                      color={statusColorMap[item.status]}
                      variant={
                        item.status === "stopped" ? "outlined" : "filled"
                      }
                    />
                    {item.broadcastJobId ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Job: ${item.broadcastJobId}`}
                      />
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Direct Message"
                      />
                    )}
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                  >
                    {item.text || "-"}
                  </Typography>

                  {item.attachmentUrl && item.attachmentType === "image" && (
                    <Box mt={1.5}>
                      <Box
                        component="img"
                        src={item.attachmentUrl}
                        alt={`attachment-${item.id}`}
                        sx={{
                          width: "100%",
                          maxWidth: 320,
                          borderRadius: 1.5,
                          border: "1px solid #e0e0e0",
                          objectFit: "cover",
                          maxHeight: 180,
                        }}
                      />
                    </Box>
                  )}

                  {item.attachmentUrl && item.attachmentType === "file" && (
                    <Box mt={1.5}>
                      <Link
                        href={item.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open attachment
                      </Link>
                    </Box>
                  )}

                  {item.attachmentUrl && item.attachmentType === "voice" && (
                    <Box mt={1.5}>
                      <Box
                        component="audio"
                        controls
                        src={item.attachmentUrl}
                        sx={{ width: "100%", maxWidth: 320 }}
                      />
                    </Box>
                  )}

                  {item.errorMessage && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      {item.errorMessage}
                    </Alert>
                  )}

                  <Box
                    mt={1.2}
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      User ID: {item.userId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Telegram Chat: {item.telegramChatId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sent At: {formatDate(item.sentAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Created At: {formatDate(item.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Attachment: {item.attachmentType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Telegram Message ID: {item.telegramMessageId ?? "-"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Box display="flex" justifyContent="center" mt={1}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="large"
          />
        </Box>
      </Box>
    </Card>
  );
}
